<template>
  <div>
    <ExSelectPicker
      :pickerLabel="pickerLabel"
      :label="label"
      :searchLabel="searchLabel"
      :items="processedCurrencies"
      :value="resolvedValue"
      :useCode="useCode"
      :isSelectedItemRemove="isSelectedItemRemove"
      :show-flags="false"
      @item-selected="handleItemSelected"
      @clear-selection="clearItemSelected"
      :isforceReadonly="isforceReadonly"
      :isShowDefautValue="isShowDefautValue"
      :defaultCurrencyProp="defaultCurrency?.code || ''"
      :isCurrencyPicker="true"
      noRecordMessage="No currency found"
      noSearchResultsMessage="No currencies found"
      @input-cleared="clearItemSelected"
      :useBackendSearch="useScrollPagination"
      :enablePagination="useScrollPagination"
      :hasMoreData="hasMoreCurrencies"
      :isLoadingMore="isLoadingMoreCurrencies"
      :isInitialLoading="isInitialCurrencyLoad"
      @search="onCurrencySearchInput"
      @load-more="loadMoreCurrencies"
    />
  </div>
</template>

<script setup>
import ExSelectPicker from "@components/SelectPicker/ExSelectPicker.vue";
import {
  defineProps,
  defineEmits,
  onMounted,
  onBeforeUnmount,
  ref,
  computed,
  watch,
  getCurrentInstance,
} from "vue";
import { getListOfCurrencies, getOperatingCurrency } from "@/api";
import { mapOperatingCurrencyRow } from "@/utils/operatingCurrencyMapper";
import configService from "../../account/src/services/config";

const CURRENCY_PAGE_SIZE = 10;

const app = getCurrentInstance();
const { proxy } = app;

const props = defineProps({
  /** Vue 2 v-model (`value` / `input`). */
  value: {
    type: Object,
    required: false,
    default: null,
  },
  /** Vue 3-style / explicit binding. */
  modelValue: {
    type: Object,
    required: false,
    default: null,
  },
  items: {
    required: false,
    type: Array,
    default() {
      return [];
    },
  },
  pickerLabel: {
    type: String,
    required: false,
    default: "Search Currency",
  },
  label: {
    type: String,
    required: false,
    default: "Select Currency",
  },
  searchLabel: {
    type: String,
    required: false,
    default: "Search Currency",
  },
  useCode: {
    type: Boolean,
    required: false,
    default: true,
  },
  isSelectedItemRemove: {
    type: Boolean,
    required: false,
    default: false,
  },
  isforceReadonly: {
    type: Boolean,
    default: false,
  },
  isShowDefautValue: {
    type: Boolean,
    default: false,
  },
  pickerPopUp: {
    type: Boolean,
    default: false,
  },
  /** Catalog currency ids already operating (greyed out in the picker). */
  disabledCatalogCurrencyIds: {
    type: Array,
    default() {
      return [];
    },
  },
  /**
   * `catalog` → GET /api/common/v2/currencies/ (add-operating-currency flow).
   * `operating` → GET /api/common/v2/account/<id>/operating-currency/ (pay range, etc.).
   */
  listSource: {
    type: String,
    default: "catalog",
    validator: (value) => ["catalog", "operating"].includes(value),
  },
  /** Parent supplies the full list (e.g. preloaded catalog); never use scroll pagination. */
  disableScrollPagination: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "input",
  "update:modelValue",
  "item-currency-selected",
  "clear-selection",
]);

/** Prefer a real selection from Vue 2 `value` or Vue 3 `modelValue`; otherwise null. */
const resolvedValue = computed(() => {
  const fromValue = props.value;
  const fromModel = props.modelValue;
  if (fromValue != null && fromValue.id != null) return fromValue;
  if (fromModel != null && fromModel.id != null) return fromModel;
  return null;
});

const currencies = ref([]);
const defaultCurrency = ref(null);
/** Next request offset; advances by each response's row count (matches API cursor). */
const currencyListOffset = ref(0);
const currencyTotalCount = ref(0);
const isLoadingMoreCurrencies = ref(false);
const isInitialCurrencyLoad = ref(false);
/** When API omits count, set from last request: full page ⇒ may have another page. */
const lastCurrencyFetchSize = ref(0);
/** Stops further pages when API returns nothing new (bad total, duplicates, or end of list). */
const currencyPaginationExhausted = ref(false);
/** Trimmed query sent to list APIs (`search` param). */
const activeCurrencySearch = ref("");
let currencySearchDebounceTimer = null;
let currencyFetchGeneration = 0;

const SEARCH_DEBOUNCE_MS = 300;

const useOperatingList = computed(() => props.listSource === "operating");

/** API scroll/search only when parent does not supply a static list. */
const useScrollPagination = computed(
  () => !props.disableScrollPagination && props.items.length === 0
);

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function apiBaseURL() {
  return (
    (configService.get && configService.get("baseURL")) ||
    process.env.VUE_APP_BASE_URL ||
    ""
  );
}

const normalizeCatalogIdKey = (id) => {
  if (id == null || id === "") return null;
  const s = String(id).trim();
  return s.length ? s : null;
};

const disabledCatalogIdSet = computed(() => {
  const out = new Set();
  for (const raw of props.disabledCatalogCurrencyIds || []) {
    const key = normalizeCatalogIdKey(raw);
    if (key != null) {
      out.add(key);
    }
  }
  return out;
});

const hasMoreCurrencies = computed(() => {
  if (!useScrollPagination.value) return false;
  if (currencyPaginationExhausted.value) return false;
  if (isInitialCurrencyLoad.value) return false;
  if (typeof currencyTotalCount.value === "number" && currencyTotalCount.value > 0) {
    return currencyListOffset.value < currencyTotalCount.value;
  }
  const loaded = currencies.value.length;
  if (loaded === 0) return false;
  return lastCurrencyFetchSize.value === CURRENCY_PAGE_SIZE;
});

const processedCurrencies = computed(() => {
  const storedCurrency = JSON.parse(localStorage.getItem("referenceCurrency"));

  // Use items prop if it has values, otherwise use fetched currencies
  const currencySource = props.items.length > 0 ? props.items : currencies.value;

  const resolveFlagUrl = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    if (!s) return null;
    if (s.startsWith("http://") || s.startsWith("https://") || s.startsWith("data:")) {
      return s;
    }
    const base =
      (configService.get && configService.get("baseURL")) ||
      process.env.VUE_APP_BASE_URL ||
      "";
    return `${base}${s}`;
  };

  const flagcdnFromCode = (code) => {
    const c = String(code || "")
      .trim()
      .toLowerCase();
    if (!c || c.length !== 2) return null;
    return `https://flagcdn.com/${c}.svg`;
  };

  return currencySource.map((currency) => {
    if (storedCurrency && currency.id === storedCurrency) {
      defaultCurrency.value = currency;
    } else if (!storedCurrency && currency.code === "USD") {
      defaultCurrency.value = currency;
    }
    const disabledFromStaticItems = props.items.some(
      (item) => item.id === currency.id && item.disabled === true
    );
    const catalogKey = normalizeCatalogIdKey(currency.id);
    const disabledFromOperating =
      catalogKey != null && disabledCatalogIdSet.value.has(catalogKey);

    const countries = currency.countries
      ?.map((country) => {
        const flag_icon =
          resolveFlagUrl(country?.flag_icon) ||
          flagcdnFromCode(country?.code) ||
          null;
        return flag_icon ? { ...country, flag_icon } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.name.localeCompare(a.name));

    const flags = Array.isArray(currency.flags)
      ? currency.flags.map((f) => resolveFlagUrl(f)).filter(Boolean)
      : [];
    const icon =
      resolveFlagUrl(currency.icon) ||
      resolveFlagUrl(currency.flag_icon) ||
      flags[0] ||
      flagcdnFromCode(currency.iso_country_code) ||
      null;

    return {
      ...currency,
      disabled: !!(disabledFromStaticItems || disabledFromOperating || currency.disabled),
      ...(countries?.length ? { countries } : { countries: undefined }),
      ...(icon ? { icon } : {}),
      ...(flags.length ? { flags } : {}),
    };
  });
});

const loadStaticCurrencyCatalog = async () => {
  if (props.items.length > 0 || useScrollPagination.value) return;
  isInitialCurrencyLoad.value = true;
  try {
    const response = await getListOfCurrencies({ page: 1, size: 500 });
    const data = response?.data;
    const rows = Array.isArray(data?.results)
      ? data.results
      : Array.isArray(data)
        ? data
        : [];
    currencies.value = normalizeCurrencyRows(rows).filter(
      (currency) => currency && currency.active !== false
    );
  } catch {
    currencies.value = [];
  } finally {
    isInitialCurrencyLoad.value = false;
  }
};

onMounted(() => {
  if (props.items.length === 0) {
    if (props.disableScrollPagination) {
      loadStaticCurrencyCatalog();
    } else {
      fetchCurrencies(false);
    }
  }
});

onBeforeUnmount(() => {
  if (currencySearchDebounceTimer) {
    clearTimeout(currencySearchDebounceTimer);
    currencySearchDebounceTimer = null;
  }
});

const onCurrencySearchInput = (raw) => {
  if (!useScrollPagination.value) return;
  const next = typeof raw === "string" ? raw.trim() : raw != null ? String(raw).trim() : "";
  if (currencySearchDebounceTimer) {
    clearTimeout(currencySearchDebounceTimer);
  }
  currencySearchDebounceTimer = setTimeout(() => {
    currencySearchDebounceTimer = null;
    activeCurrencySearch.value = next;
    fetchCurrencies(false);
  }, SEARCH_DEBOUNCE_MS);
};

/**
 * @param {object|null} selectedItem
 * @param {{ notifyParent?: boolean }} [options]
 * notifyParent=false → silent hydrate on edit (avoids marking the modal field dirty).
 */
const syncPickerValue = (selectedItem, { notifyParent = true } = {}) => {
  emit("input", selectedItem);
  emit("update:modelValue", selectedItem);
  if (notifyParent) {
    emit("item-currency-selected", selectedItem);
  }
};

function flagcdnFromCode(code) {
  const c = String(code || "")
    .trim()
    .toLowerCase();
  if (!c || c.length !== 2) return null;
  return `https://flagcdn.com/${c}.svg`;
}

/**
 * Closed-field display must use a single `icon` (same as CountryPicker → Avatar).
 * Countries are flattened to a single `icon` rather than kept as an array.
 */
function normalizeCurrencySelectionForPicker(currency) {
  if (!currency || typeof currency !== "object") return currency;

  const rawCountries = Array.isArray(currency.countries) ? currency.countries : [];
  const countriesWithFlags = rawCountries.filter(
    (country) => country?.flag_icon || country?.icon
  );
  const flags = Array.isArray(currency.flags)
    ? currency.flags.filter(Boolean)
    : [];
  const icon =
    currency.icon ||
    currency.flag_icon ||
    countriesWithFlags[0]?.flag_icon ||
    countriesWithFlags[0]?.icon ||
    flags[0] ||
    flagcdnFromCode(currency.iso_country_code) ||
    flagcdnFromCode(rawCountries[0]?.code) ||
    null;
  const { countries: _countries, ...rest } = currency;

  return icon ? { ...rest, icon } : rest;
}

const handleItemSelected = (selectedItem) => {
  syncPickerValue(normalizeCurrencySelectionForPicker(selectedItem));
};

const clearItemSelected = () => {
  emit("input", null);
  emit("update:modelValue", null);
  if (props.pickerPopUp) {
    emit("item-currency-selected", null);
  }
  emit("clear-selection");
};

const normalizeCurrencyRows = (results) => {
  if (!Array.isArray(results)) return [];
  if (!useOperatingList.value) {
    return results.map((c) => ({ ...c }));
  }
  const baseURL = apiBaseURL();
  return results.map((row) => mapOperatingCurrencyRow(row, baseURL));
};

const mergeCurrencyPages = (existing, incoming) => {
  const byId = new Map();
  for (const c of existing) {
    if (c && c.id != null) byId.set(String(c.id), c);
  }
  for (const c of incoming) {
    if (c && c.id != null) byId.set(String(c.id), c);
  }
  return Array.from(byId.values());
};

const fetchCurrencyPage = async ({ offset, search }) => {
  if (useOperatingList.value) {
    const accountId = getAccountId();
    return getOperatingCurrency(accountId, {
      offset,
      size: CURRENCY_PAGE_SIZE,
      limit: CURRENCY_PAGE_SIZE,
      search: search || undefined,
      status: ["active"],
    });
  }
  const params = {
    limit: CURRENCY_PAGE_SIZE,
    offset,
  };
  if (search) {
    params.search = search;
  }
  return getListOfCurrencies(params);
};

const fetchCurrencies = async (append = false) => {
  if (props.items.length > 0) return;
  let showGlobalLoader = false;
  if (append) {
    if (isLoadingMoreCurrencies.value || !hasMoreCurrencies.value) return;
    isLoadingMoreCurrencies.value = true;
  } else {
    isLoadingMoreCurrencies.value = false;
    isInitialCurrencyLoad.value = true;
    currencyPaginationExhausted.value = false;
    currencyListOffset.value = 0;
    showGlobalLoader = !props.pickerPopUp && currencies.value.length === 0;
    if (showGlobalLoader) {
      proxy.$loader.show();
    }
  }
  const requestOffset = append ? currencyListOffset.value : 0;
  // Fresh fetches invalidate in-flight work; appends keep the same generation.
  const generation = append ? currencyFetchGeneration : ++currencyFetchGeneration;
  try {
    const q = activeCurrencySearch.value.trim();
    const response = await fetchCurrencyPage({
      offset: requestOffset,
      search: q,
    });
    if (generation !== currencyFetchGeneration) {
      return;
    }
    const raw = response?.data?.results ?? [];
    // Advance by the API page size (not the active-only subset) so scroll
    // pagination stays aligned with the backend cursor.
    lastCurrencyFetchSize.value = raw.length;
    const count = typeof response?.data?.count === "number" ? response.data.count : null;
    if (count != null) {
      currencyTotalCount.value = count;
    }
    // AC: picker only lists active currencies / operating currencies.
    const batch = normalizeCurrencyRows(raw).filter(
      (currency) => currency && currency.active !== false
    );
    const nextOffset = requestOffset + raw.length;
    if (append) {
      currencies.value = mergeCurrencyPages(currencies.value, batch);
      currencyListOffset.value = nextOffset;
      if (raw.length === 0) {
        currencyPaginationExhausted.value = true;
      }
    } else {
      currencies.value = batch;
      currencyListOffset.value = nextOffset;
      if (count == null) {
        currencyTotalCount.value = 0;
      }
    }
  } catch {
    if (generation !== currencyFetchGeneration) {
      return;
    }
    if (!append) {
      currencies.value = [];
      currencyTotalCount.value = 0;
    }
    if (append) {
      currencyPaginationExhausted.value = true;
    }
  } finally {
    if (append) {
      isLoadingMoreCurrencies.value = false;
    } else if (generation === currencyFetchGeneration) {
      isInitialCurrencyLoad.value = false;
      if (showGlobalLoader) {
        proxy.$loader.hide();
      }
    }
  }
};

const loadMoreCurrencies = () => {
  if (!useScrollPagination.value || isLoadingMoreCurrencies.value || !hasMoreCurrencies.value) {
    return;
  }
  fetchCurrencies(true);
};

const hasDisplayableFlagIcon = (currency) => {
  if (!currency) return false;
  if (currency.icon || currency.flag_icon) return true;
  return (
    Array.isArray(currency.countries) &&
    currency.countries.some((country) => country?.flag_icon || country?.icon)
  );
};

function findCurrencyMatch(current) {
  if (!current?.id) return null;
  return (
    processedCurrencies.value.find(
      (currency) => currency?.id != null && String(currency.id) === String(current.id)
    ) || null
  );
}

function applyEnrichedSelection(current, match) {
  const normalized = normalizeCurrencySelectionForPicker({
    ...match,
    ...current,
    id: current.id,
    name: current.name || match.name,
    code: current.code || match.code,
    symbol: current.symbol || match.symbol,
    iso_country_code: current.iso_country_code || match.iso_country_code,
    countries: match.countries,
    icon: match.icon || current.icon,
    flags: match.flags || current.flags,
  });
  if (!hasDisplayableFlagIcon(normalized)) return false;
  // Silent: edit hydrate must not fire item-currency-selected (marks modal dirty).
  syncPickerValue(normalized, { notifyParent: false });
  return true;
}

/** When editing, parent may pass id/code/name only — merge flags from loaded list. */
function enrichSelectionFromList() {
  const current = resolvedValue.value;
  if (!current?.id) return;
  if (hasDisplayableFlagIcon(current)) {
    // Flatten countries → icon so ExSelectPicker uses Avatar like CountryPicker.
    const flattened = normalizeCurrencySelectionForPicker(current);
    if (
      flattened?.icon &&
      (flattened.icon !== current.icon ||
        (Array.isArray(current.countries) && current.countries.length))
    ) {
      syncPickerValue(flattened, { notifyParent: false });
    }
    return;
  }

  const match = findCurrencyMatch(current);
  if (match && applyEnrichedSelection(current, match)) return;
}

/**
 * Edit selections are often not on the first scroll page — search by code/name
 * so we can resolve an icon the same way CountryPicker gets one from initialValues.
 */
let hydrateInFlight = false;
async function hydrateSelectedCurrencyFlag() {
  const current = resolvedValue.value;
  if (!current?.id || hasDisplayableFlagIcon(current)) return;
  if (!useScrollPagination.value || hydrateInFlight) return;

  const match = findCurrencyMatch(current);
  if (match && applyEnrichedSelection(current, match)) return;

  const search = String(current.code || current.name || "").trim();
  if (!search) return;

  hydrateInFlight = true;
  try {
    const response = await fetchCurrencyPage({ offset: 0, search });
    const raw = response?.data?.results ?? [];
    const batch = normalizeCurrencyRows(raw).filter(
      (currency) => currency && currency.active !== false
    );
    currencies.value = mergeCurrencyPages(currencies.value, batch);
    const found =
      batch.find((c) => c?.id != null && String(c.id) === String(current.id)) ||
      batch.find(
        (c) =>
          current.code &&
          String(c.code || "").toUpperCase() === String(current.code).toUpperCase()
      );
    if (found) {
      applyEnrichedSelection(current, found);
    }
  } catch {
    /* keep selection without icon */
  } finally {
    hydrateInFlight = false;
  }
}

watch(processedCurrencies, enrichSelectionFromList, { deep: true });
watch(
  resolvedValue,
  () => {
    enrichSelectionFromList();
    hydrateSelectedCurrencyFlag();
  },
  { deep: true, immediate: true }
);
</script>

<style scoped>
/* Match CountryPicker so Avatar flag is visible when selection uses `icon`. */
::v-deep(.selected-currency-icon) {
  width: 24px !important;
  height: 24px !important;
}

::v-deep(.selected-currency-icon img) {
  height: 100% !important;
  width: 100% !important;
  object-fit: cover !important;
  margin-left: 0px !important;
  z-index: 1;
}

::v-deep(.v-avatar) {
  overflow: visible !important;
}

::v-deep .custom-response-picker {
  width: 100%;
}

.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100px;
  gap: 12px;
}

.loading-text {
  margin: 0;
  font-size: 14px;
  color: #666;
  text-align: center;
}
</style>
